// MS50 #2964 — Stimulus "theme-switcher" controller.
//
// Replaces the one-shot `DOMContentLoaded` click-binding that used to live in
// dark_mode_switcher.js. Under Turbo Drive, DOMContentLoaded fires only on the
// first full page load, so after any in-app Turbo navigation the freshly
// rendered toggle buttons had no click listeners and the theme could no longer
// be changed (the #2964 defect). Stimulus re-runs connect() every time the
// element re-enters the DOM, so the toggle keeps working after every
// navigation.
//
// Boot-time first-paint — setTheme(getPreferredTheme()) at module eval plus the
// prefers-color-scheme change listener — still lives in dark_mode_switcher.js
// so the correct theme is applied before this controller connects.
import { Controller } from "@hotwired/stimulus"

const getStoredTheme = () => localStorage.getItem("theme")
const setStoredTheme = (theme) => localStorage.setItem("theme", theme)

// Preference and resolved color scheme are intentionally separate. With no
// stored choice the preference is Auto, while the rendered document follows
// the operating system's current light/dark scheme (#3293).
const getPreferredTheme = () => getStoredTheme() || "auto"

const getResolvedTheme = (theme) => {
  if (theme === "auto") {
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light"
  }

  return theme
}

const setTheme = (theme) => {
  document.documentElement.setAttribute("data-bs-theme", getResolvedTheme(theme))
}

export default class extends Controller {
  // Runs on initial load AND after every Turbo render — reflect the active
  // theme in the dropdown (icon swap + aria state).
  connect() {
    this.showActiveTheme(getPreferredTheme())
  }

  // Click handler for each [data-bs-theme-value] button.
  set(event) {
    const theme = event.currentTarget.dataset.bsThemeValue
    setStoredTheme(theme)
    setTheme(theme)
    this.showActiveTheme(theme, true)
  }

  showActiveTheme(theme, focus = false) {
    const themeSwitcher = this.element.querySelector("#bd-theme")

    if (!themeSwitcher) {
      return
    }

    const themeSwitcherText = this.element.querySelector("#bd-theme-text")
    const themeModeLabel = this.element.querySelector(".theme-mode-label")
    const activeThemeIcon = this.element.querySelector(".theme-icon-active use")
    const btnToActive = this.element.querySelector(`[data-bs-theme-value="${theme}"]`)
    if (!btnToActive) {
      return
    }

    const svgOfActiveBtn = btnToActive.querySelector("svg use").getAttribute("href")

    this.element.querySelectorAll("[data-bs-theme-value]").forEach((element) => {
      element.classList.remove("active")
      element.setAttribute("aria-pressed", "false")
    })

    btnToActive.classList.add("active")
    btnToActive.setAttribute("aria-pressed", "true")
    activeThemeIcon.setAttribute("href", svgOfActiveBtn)
    const selectedPreference = btnToActive.dataset.bsThemeValue
    const selectedPreferenceLabel = selectedPreference.charAt(0).toUpperCase() + selectedPreference.slice(1)
    if (themeModeLabel) {
      themeModeLabel.textContent = selectedPreferenceLabel
    }

    const themeSwitcherLabel = `${themeSwitcherText.textContent} (${selectedPreferenceLabel})`
    themeSwitcher.setAttribute("aria-label", themeSwitcherLabel)

    if (focus) {
      themeSwitcher.focus()
    }
  }
}
